import React, {useCallback, useEffect} from 'react';
import {useTranslation} from 'react-i18next';

import useThreeSixtyNewStore from 'common/stores/threeSixtyStore';
import {shallow} from 'zustand/shallow';
import {Layout, Table, Text} from '@phonectas/unifon-ui-kit';
import {LIMIT_PAYLOAD} from 'common/constants';
import {StyledSectionDiv} from '../../styled';
import {BillingGroup} from 'common/types/store/threeSixtyTypes';

interface BillingGroupTableProps {
	id: string;
}

const BillingGroupTable: React.FC<BillingGroupTableProps> = ({id}) => {
	const [t] = useTranslation();
	const {getBillingGroups} = useThreeSixtyNewStore((state) => ({getBillingGroups: state.getBillingGroups}), shallow);
	const {billingGroups, totalCount, accountNotFound} = useThreeSixtyNewStore((state) => ({
		billingGroups: state.accountTables?.billingGroups,
		totalCount: state.accountTables?.billingGroups?.total_count,
		accountNotFound: state.accountNotFound,
	}), shallow);
	const billingGroupsData = billingGroups?.billing_groups;

	const onLoadMore = useCallback((size: number) => {
		if (totalCount && totalCount <= size) return;
		getBillingGroups({id: id, limit: size + LIMIT_PAYLOAD});
	}, [id, totalCount, getBillingGroups]);

	const renderItem = useCallback((item: BillingGroup, index: number) => (
		<Table.Row
			key={index}
			index={index}
			style={{padding: '0px'}}
		>
			<Table.Cell>
				{item?.name ? item?.name : ''}
			</Table.Cell>
			<Table.Cell>
				{item?.owner ? item?.owner : ''}
			</Table.Cell>
			<Table.Cell>
				{item?.company_vat_no ? item?.company_vat_no : ''}
			</Table.Cell>
			<Table.Cell>
				{item?.total_outstanding ? item?.total_outstanding : ''}
			</Table.Cell>
			<Table.Cell>
				{item?.inv_past_due_count ? item?.inv_past_due_count : ''}
			</Table.Cell>
			<Table.Cell>
				{item?.mobile_count ? item?.mobile_count : ''}
			</Table.Cell>
			<Table.Cell>
				{item?.mbb_count ? item?.mbb_count : ''}
			</Table.Cell>
			<Table.Cell>
				{item?.fixed_count ? item?.fixed_count : ''}
			</Table.Cell>
		</Table.Row>
	), []);

	useEffect(() => {
		if (accountNotFound) return;
		if (totalCount === 0 && billingGroups?.billing_groups === null && billingGroups?.filters?.account_id === id) return;

		const hasBillingGroupsData = Array.isArray(billingGroupsData) && billingGroupsData.length > 0;
		const isAccountIdMatched = hasBillingGroupsData && id === billingGroupsData[0]?.account_id;
		const isBillingGroupFetched = billingGroups !== null && isAccountIdMatched;
		const isBillingGroupsFilterMatched = billingGroups?.filters?.account_id === id && billingGroupsData == null;

		if (isBillingGroupFetched || isBillingGroupsFilterMatched) return;

		getBillingGroups({id: id, limit: LIMIT_PAYLOAD});
	}, [id, billingGroups, billingGroupsData, accountNotFound, getBillingGroups, totalCount]);

	if (!billingGroups || !billingGroups?.billing_groups) {
		return (
			<StyledSectionDiv
				$endDiv
				$fullHeight
			>
				<Layout
					fullHeight
					fullWidth
					alignItems="center"
					justifyContent="center"
				>
					<Text>{t('No data')}</Text>
				</Layout>
			</StyledSectionDiv>
		);
	}

	return (
		<StyledSectionDiv
			style={{
				display: 'flex',
				flexDirection: 'column',
				flexGrow: '1',
				overflow: 'hidden',
				padding: '16px 8px'
			}}
			$endDiv
			$fullHeight
		>
			<Table
				items={billingGroupsData || []}
				renderItem={(item, index) => renderItem(item, index)}
				virtualized
				onScrollDown={onLoadMore}
			>
				<Table.Row style={{padding: '0px'}}>
					<Table.Cell>{t('Name')}</Table.Cell>
					<Table.Cell>{t('Owner')}</Table.Cell>
					<Table.Cell>{t('Org')}</Table.Cell>
					<Table.Cell>{t('Balance')}</Table.Cell>
					<Table.Cell>{t('Invoices due')}</Table.Cell>
					<Table.Cell>{t('Mobile')}</Table.Cell>
					<Table.Cell>{t('Broadband')}</Table.Cell>
					<Table.Cell>{t('Fixed')}</Table.Cell>
				</Table.Row>
			</Table>
		</StyledSectionDiv>
	);
}

export default React.memo(BillingGroupTable);